import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AdminInviteAcknowledgementPopup from 'src/app/AdminInviteAcknowledgementPopup/AdminInviteAcknowledgementPopup'
import {
  selectContactsFullPaginatedData,
  selectFilteredContactEmailOptions,
} from 'src/app/InvitePeople/redux/invitePeople.selectors'
import {
  selectPostingOrganizationInvitesFromState,
  selectPostInvitesTopLevelErrorFromState,
} from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { getUserConnections } from 'src/app/PeopleContacts/redux/peopleContacts.actions'
import ShowHover from 'src/assets/svgs/Show-Hover.svg'
import { AccountAccessOptionsFields } from 'src/components/AccountAccessOptions/AccountAccessOptions'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import EnterEmailsForm from 'src/components/EnterEmailsForm/EnterEmailsForm'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import {
  BookkeeperAccountVisibilityTypes,
  ImageNames,
  OrganizationRoles,
} from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { postInviteToOrganizationAndOpenToastAndCloseFlyout } from 'src/redux/actions/data/organizations.actions'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'
import { css } from 'styled-components'

interface InviteToOrgProps {
  prePopulatedEmails?: string[]
}

const InviteToOrg: React.FC<InviteToOrgProps> = (props) => {
  const translate = useTranslate()

  const postingOrganizationInvites = useSelector(
    selectPostingOrganizationInvitesFromState,
  )
  const topLevelError = useSelector(selectPostInvitesTopLevelErrorFromState)
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const { analytics } = useAnalyticsContext()
  const contactsFullPaginatedData = useSelector(selectContactsFullPaginatedData)
  const filteredUserEmailOptions = useSelector(
    selectFilteredContactEmailOptions,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: userOrganization?.id,
  })

  const [
    isAdminInviteAcknowledgementPopupOpen,
    setAdminInviteAcknowledgementPopupOpen,
  ] = useState(false)
  const [emails, setEmails] = useState<string[]>([])
  const [hasPostedOrgInvite, setHasPostedOrgInvite] = useState<boolean>(false)
  const [role, setRole] = useState<OrganizationRoles>(OrganizationRoles.member)
  const [bookkeeperAccess, setBookkeeperAccess] =
    useState<AccountAccessOptionsFields>({
      grantFullAccess: true,
      selectiveAccessAccountIds: [],
    })
  const [maxUsers, setMaxUsers] = useState<number>(100)
  const organizationInviteImageSrc = useColorOrGrayscaleImage(
    ImageNames.OrganizationInvite,
  )

  useEffect(() => {
    if (postingOrganizationInvites) {
      setHasPostedOrgInvite(true)
    }
  }, [postingOrganizationInvites])

  const dispatch = useFlyoutDispatch()

  const fetchContacts = (filterInput?: string, page?: number): void => {
    dispatch(
      getUserConnections({
        search: filterInput,
        page: page || 0,
        count: 10,
      }),
    )
  }

  // TODO: @bjunya-extend refactor to use `userQuery` instead
  useEffect(() => {
    dispatch(
      getCreditCards({
        sortField: 'userPendingActiveFirst',
        type: CreditCardType.Source,
        all: true,
      }),
    )
  }, [])

  const contactInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchContacts,
    ...contactsFullPaginatedData,
  }

  const dispatchPostUserInvites = (
    body: PostPeopleInvitesRequest,
    sendAdminInvite?: boolean,
  ): void => {
    setEmails(body.emails)
    const analyticsProperties =
      bookkeeperAccess.selectiveAccessAccountIds !== undefined
        ? {
            screen: '/people/add',
            interaction: 'button press',
            intent: 'invite to org',
            count: body.emails.length,
            invitee_org_role: role,
            num_accounts_granted:
              bookkeeperAccess.selectiveAccessAccountIds.length,
          }
        : {
            screen: '/people/add',
            interaction: 'button press',
            intent: 'invite to org',
            count: body.emails.length,
            invitee_org_role: role,
          }
    if (role === OrganizationRoles.bookkeeper) {
      dispatch(
        postInviteToOrganizationAndOpenToastAndCloseFlyout(
          userOrganization?.id || '',
          {
            ...body,
            role: role,
            bookkeeperAccountVisibilityType: bookkeeperAccess.grantFullAccess
              ? BookkeeperAccountVisibilityTypes.ALL
              : BookkeeperAccountVisibilityTypes.SELECTIVE,
            bookkeeperAccounts: bookkeeperAccess.selectiveAccessAccountIds,
          },
        ),
      )
    } else if (
      role === OrganizationRoles.admin &&
      orgFeatureFlags?.ownerAdminEnhancementsEnabled &&
      !sendAdminInvite
    ) {
      setAdminInviteAcknowledgementPopupOpen(true)
    } else {
      dispatch(
        postInviteToOrganizationAndOpenToastAndCloseFlyout(
          userOrganization?.id || '',
          { ...body, role: role },
        ),
      )
    }
    analytics?.track('add people - invite to org', analyticsProperties)
  }

  const updateRole = (role: OrganizationRoles): void => {
    setRole(role)
  }

  const updateSelectedAccounts = (fields: AccountAccessOptionsFields): void => {
    setBookkeeperAccess({ ...fields })
  }

  useEffect(() => {
    switch (role) {
      case OrganizationRoles.admin:
      case OrganizationRoles.bookkeeper:
        setMaxUsers(30)
        break
      case OrganizationRoles.cardManager:
      case OrganizationRoles.member:
      case OrganizationRoles.guest:
        setMaxUsers(100)
        break
    }
  }, [role])

  return (
    <>
      {isAdminInviteAcknowledgementPopupOpen &&
        orgFeatureFlags?.ownerAdminEnhancementsEnabled && (
          <AdminInviteAcknowledgementPopup
            selectedEmailsNotInOrg={emails || []}
            submitFunction={dispatchPostUserInvites}
            closeFunction={setAdminInviteAcknowledgementPopupOpen}
          />
        )}
      <ActionRightFlyoutContent
        title={translate('Invite People')}
        specificElements={{
          imageSrc: organizationInviteImageSrc,
          rotateImage: true,
        }}
      >
        {!orgFeatureFlags?.ownerAdminEnhancementsEnabled && (
          <p
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 30px;
            `}
          >
            <img
              src={ShowHover}
              alt=""
              css={css`
                filter: grayscale(100%);
                width: 12px;
                margin-right: 4px;
              `}
            />
            {translate(
              '${bold} Visit our ${resourceCenter} to learn more about inviting your team.',
              {
                bold: (
                  <span
                    css={css`
                      ${getFontCss(
                        12,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.medium,
                      )};
                    `}
                  >
                    {translate(
                      'The members list is visible to everyone by default. You can turn this setting off in Org Settings.',
                    )}
                  </span>
                ),
                resourceCenter: (
                  <ResourceCenterLink
                    size={HoverableLinkSizes.Small}
                    underline
                    regular
                  />
                ),
              },
            )}
          </p>
        )}
        <EnterEmailsForm
          data-testid="invite-email"
          postingUserInvites={postingOrganizationInvites}
          topLevelError={hasPostedOrgInvite ? topLevelError : undefined}
          postUserInvites={dispatchPostUserInvites}
          buttonText={translate('Confirm and Invite')}
          showCounter
          maxInputNumber={maxUsers}
          prePopulatedEmails={props.prePopulatedEmails}
          dropdownProps={{
            infiniteScrollProps: contactInfiniteScrollProps,
            userEmailOptions: filteredUserEmailOptions,
          }}
          showRoles
          setRole={updateRole}
          setBookkeeperAccess={updateSelectedAccounts}
          dontShowMaxInputError
        />
      </ActionRightFlyoutContent>
    </>
  )
}

export default InviteToOrg
